'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { Bell, AlertTriangle, FileText, ShieldAlert, TrendingUp, X, CheckCheck, Filter, ArrowLeft, Clock, Building2, ExternalLink, Wifi, FileDown } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { exportCSV, exportPDF } from '@/lib/exportUtils';

type NotificationCategory = 'all' | 'critical' | 'contracts' | 'dd' | 'risk';
type NotificationSeverity = 'critical' | 'high' | 'medium' | 'low';

interface Notification {
  id: string;
  category: 'critical' | 'contracts' | 'dd' | 'risk';
  severity: NotificationSeverity;
  title: string;
  description: string;
  vendor?: string;
  timestamp: string;
  timeAgo: string;
  actionLabel: string;
  actionHref: string;
  dismissed: boolean;
  read: boolean;
  isRealtime?: boolean;
}

const INITIAL_NOTIFICATIONS: Notification[] = [
  {
    id: 'n-001',
    category: 'critical',
    severity: 'critical',
    title: 'Critical Security Breach Detected',
    description: 'Vendor Acme Cloud Services reported a data breach affecting 12,000 city records. Immediate review and containment required.',
    vendor: 'Acme Cloud Services',
    timestamp: '2026-03-29T17:30:00',
    timeAgo: '29 min ago',
    actionLabel: 'View Incident',
    actionHref: '/issues',
    dismissed: false,
    read: false,
  },
  {
    id: 'n-002',
    category: 'critical',
    severity: 'critical',
    title: 'Vendor Risk Score Exceeded Threshold',
    description: 'DataBridge Inc. risk score jumped to 87/100 following failed penetration test. Automatic escalation triggered.',
    vendor: 'DataBridge Inc.',
    timestamp: '2026-03-29T16:45:00',
    timeAgo: '1 hr ago',
    actionLabel: 'Review Risk',
    actionHref: '/vendor-management',
    dismissed: false,
    read: false,
  },
  {
    id: 'n-003',
    category: 'contracts',
    severity: 'high',
    title: 'Contract Expiring in 7 Days',
    description: 'Master Service Agreement with TechVault Solutions expires on April 5, 2026. Renewal or termination decision required.',
    vendor: 'TechVault Solutions',
    timestamp: '2026-03-29T09:00:00',
    timeAgo: '9 hrs ago',
    actionLabel: 'View Contract',
    actionHref: '/contracts',
    dismissed: false,
    read: false,
  },
  {
    id: 'n-004',
    category: 'contracts',
    severity: 'high',
    title: 'Contract Expiring in 14 Days',
    description: 'SaaS License Agreement with CivicNet Corp expires on April 12, 2026. Auto-renewal clause requires 10-day notice to cancel.',
    vendor: 'CivicNet Corp',
    timestamp: '2026-03-29T08:00:00',
    timeAgo: '10 hrs ago',
    actionLabel: 'View Contract',
    actionHref: '/contracts',
    dismissed: false,
    read: true,
  },
  {
    id: 'n-005',
    category: 'contracts',
    severity: 'medium',
    title: 'Contract Expiring in 30 Days',
    description: 'Professional Services Agreement with Urban Analytics Ltd expires on April 28, 2026. Begin renewal negotiations.',
    vendor: 'Urban Analytics Ltd',
    timestamp: '2026-03-28T14:00:00',
    timeAgo: '1 day ago',
    actionLabel: 'View Contract',
    actionHref: '/contracts',
    dismissed: false,
    read: true,
  },
  {
    id: 'n-006',
    category: 'dd',
    severity: 'high',
    title: 'Critical DD Finding: Missing SOC 2 Report',
    description: 'Due diligence review for Nexus Payments flagged missing SOC 2 Type II report. Vendor has 5 business days to provide documentation.',
    vendor: 'Nexus Payments',
    timestamp: '2026-03-29T11:15:00',
    timeAgo: '7 hrs ago',
    actionLabel: 'Open DD Review',
    actionHref: '/due-diligence-workspace',
    dismissed: false,
    read: false,
  },
  {
    id: 'n-007',
    category: 'dd',
    severity: 'high',
    title: 'DD Assessment Overdue',
    description: 'Annual security assessment for CloudBase Systems is 12 days overdue. Escalation to CISO required per policy.',
    vendor: 'CloudBase Systems',
    timestamp: '2026-03-28T09:00:00',
    timeAgo: '1 day ago',
    actionLabel: 'Open DD Review',
    actionHref: '/due-diligence-workspace',
    dismissed: false,
    read: true,
  },
  {
    id: 'n-008',
    category: 'dd',
    severity: 'medium',
    title: 'Privacy Impact Assessment Required',
    description: 'New data processing scope for SmartCity IoT requires a Privacy Impact Assessment before go-live scheduled April 15.',
    vendor: 'SmartCity IoT',
    timestamp: '2026-03-27T16:00:00',
    timeAgo: '2 days ago',
    actionLabel: 'Start Assessment',
    actionHref: '/due-diligence-workspace',
    dismissed: false,
    read: true,
  },
  {
    id: 'n-009',
    category: 'risk',
    severity: 'critical',
    title: 'Risk Escalation: Subcontractor Breach',
    description: 'Fourth-party risk alert: Subcontractor of GovTech Partners reported ransomware attack. Supply chain exposure assessment needed.',
    vendor: 'GovTech Partners',
    timestamp: '2026-03-29T15:00:00',
    timeAgo: '3 hrs ago',
    actionLabel: 'Assess Exposure',
    actionHref: '/monitoring',
    dismissed: false,
    read: false,
  },
  {
    id: 'n-010',
    category: 'risk',
    severity: 'high',
    title: 'Insurance Certificate Expired',
    description: 'General liability insurance for Meridian Consulting expired March 25, 2026. Vendor cannot perform work until renewed.',
    vendor: 'Meridian Consulting',
    timestamp: '2026-03-29T08:30:00',
    timeAgo: '9 hrs ago',
    actionLabel: 'View Vendor',
    actionHref: '/vendor-management',
    dismissed: false,
    read: false,
  },
  {
    id: 'n-011',
    category: 'risk',
    severity: 'medium',
    title: 'Monitoring Alert: Uptime Below SLA',
    description: 'CityPortal SaaS uptime dropped to 97.2% this month, below the 99.5% SLA threshold. Penalty clause may apply.',
    vendor: 'CityPortal SaaS',
    timestamp: '2026-03-28T12:00:00',
    timeAgo: '1 day ago',
    actionLabel: 'View Monitoring',
    actionHref: '/monitoring',
    dismissed: false,
    read: true,
  },
  {
    id: 'n-012',
    category: 'risk',
    severity: 'low',
    title: 'Vendor Questionnaire Response Pending',
    description: 'Annual vendor questionnaire sent to 3 vendors remains unanswered after 14 days. Send reminder or escalate.',
    vendor: undefined,
    timestamp: '2026-03-27T10:00:00',
    timeAgo: '2 days ago',
    actionLabel: 'Send Reminder',
    actionHref: '/monitoring',
    dismissed: false,
    read: true,
  },
];

const CATEGORY_CONFIG = {
  critical: {
    label: 'Critical Events',
    icon: AlertTriangle,
    color: 'text-red-600 dark:text-red-400',
    bg: 'bg-red-50 dark:bg-red-950/40',
    border: 'border-red-200 dark:border-red-800',
    dot: 'bg-red-500',
    badgeBg: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400',
  },
  contracts: {
    label: 'Expiring Contracts',
    icon: FileText,
    color: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-50 dark:bg-amber-950/40',
    border: 'border-amber-200 dark:border-amber-800',
    dot: 'bg-amber-500',
    badgeBg: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400',
  },
  dd: {
    label: 'DD Findings',
    icon: ShieldAlert,
    color: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-50 dark:bg-blue-950/40',
    border: 'border-blue-200 dark:border-blue-800',
    dot: 'bg-blue-500',
    badgeBg: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400',
  },
  risk: {
    label: 'Risk Escalations',
    icon: TrendingUp,
    color: 'text-purple-600 dark:text-purple-400',
    bg: 'bg-purple-50 dark:bg-purple-950/40',
    border: 'border-purple-200 dark:border-purple-800',
    dot: 'bg-purple-500',
    badgeBg: 'bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-400',
  },
};

const SEVERITY_LABELS: Record<NotificationSeverity, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

const SEVERITY_BADGE: Record<NotificationSeverity, string> = {
  critical: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400',
  high: 'bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-400',
  medium: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400',
  low: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
};

const NOTIF_EXPORT_COLUMNS = [
  { header: 'Category', key: 'category' },
  { header: 'Severity', key: 'severity' },
  { header: 'Title', key: 'title' },
  { header: 'Description', key: 'description' },
  { header: 'Vendor', key: 'vendor' },
  { header: 'Timestamp', key: 'timestamp' },
  { header: 'Status', key: 'status' },
];

function timeAgoFromDate(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr${diffHr > 1 ? 's' : ''} ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
}

export default function NotificationsScreen() {
  const [notifications, setNotifications] = useState<Notification[]>(INITIAL_NOTIFICATIONS);
  const [activeCategory, setActiveCategory] = useState<NotificationCategory>('all');
  const [showDismissed, setShowDismissed] = useState(false);
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  const notifCounterRef = useRef(100);

  // ── Real-time Supabase subscriptions ──────────────────────────────────────
  useEffect(() => {
    const supabase = createClient();

    // Helper to prepend a new notification
    const addNotification = (notif: Notification) => {
      setNotifications((prev) => {
        // Avoid duplicates by id prefix
        if (prev.some((n) => n.id === notif.id)) return prev;
        return [notif, ...prev];
      });
    };

    // ── Issues: new critical/high issues → "Critical Events"
    const issuesChannel = supabase
      .channel('rt_notifications_issues')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'issues' },
        (payload) => {
          const row = payload.new as Record<string, unknown>;
          const severity = (row.severity as string) || 'High';
          const isCritical = severity === 'Critical';
          const id = `rt-issue-${notifCounterRef.current++}`;
          const ts = (row.created_at as string) || new Date().toISOString();
          addNotification({
            id,
            category: 'critical',
            severity: isCritical ? 'critical' : 'high',
            title: isCritical ? 'New Critical Issue Raised' : 'New High-Severity Issue',
            description: `${row.title || 'An issue'} has been logged for ${row.vendor_name || 'a vendor'}. ${row.description || ''}`.trim(),
            vendor: (row.vendor_name as string) || undefined,
            timestamp: ts,
            timeAgo: timeAgoFromDate(ts),
            actionLabel: 'View Issue',
            actionHref: '/issues',
            dismissed: false,
            read: false,
            isRealtime: true,
          });
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'issues' },
        (payload) => {
          const row = payload.new as Record<string, unknown>;
          const severity = (row.severity as string) || '';
          if (severity !== 'Critical' && severity !== 'High') return;
          const id = `rt-issue-upd-${notifCounterRef.current++}`;
          const ts = (row.updated_at as string) || new Date().toISOString();
          addNotification({
            id,
            category: 'critical',
            severity: severity === 'Critical' ? 'critical' : 'high',
            title: 'Issue Status Updated',
            description: `Issue "${row.title || ''}" for ${row.vendor_name || 'a vendor'} was updated to status: ${row.issue_status || ''}.`,
            vendor: (row.vendor_name as string) || undefined,
            timestamp: ts,
            timeAgo: timeAgoFromDate(ts),
            actionLabel: 'View Issue',
            actionHref: '/issues',
            dismissed: false,
            read: false,
            isRealtime: true,
          });
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') setRealtimeConnected(true);
      });

    // ── Vendors: risk tier changes → "Risk Escalations"
    const vendorsChannel = supabase
      .channel('rt_notifications_vendors')
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'vendors' },
        (payload) => {
          const oldRow = payload.old as Record<string, unknown>;
          const newRow = payload.new as Record<string, unknown>;
          const oldTier = oldRow.risk_tier as string;
          const newTier = newRow.risk_tier as string;
          const riskEscalated =
            (newTier === 'Critical' && oldTier !== 'Critical') ||
            (newTier === 'High' && oldTier !== 'High' && oldTier !== 'Critical');
          if (!riskEscalated) return;
          const id = `rt-vendor-${notifCounterRef.current++}`;
          const ts = (newRow.updated_at as string) || new Date().toISOString();
          addNotification({
            id,
            category: 'risk',
            severity: newTier === 'Critical' ? 'critical' : 'high',
            title: `Vendor Risk Tier Escalated to ${newTier}`,
            description: `${newRow.name || 'A vendor'} risk tier changed from ${oldTier} to ${newTier}. Immediate review may be required.`,
            vendor: (newRow.name as string) || undefined,
            timestamp: ts,
            timeAgo: timeAgoFromDate(ts),
            actionLabel: 'Review Vendor',
            actionHref: '/vendor-management',
            dismissed: false,
            read: false,
            isRealtime: true,
          });
        }
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'vendors' },
        (payload) => {
          const row = payload.new as Record<string, unknown>;
          const id = `rt-vendor-new-${notifCounterRef.current++}`;
          const ts = (row.created_at as string) || new Date().toISOString();
          addNotification({
            id,
            category: 'risk',
            severity: 'medium',
            title: 'New Vendor Added',
            description: `${row.name || 'A new vendor'} has been added to the system in the ${row.lifecycle_stage || 'Intake'} stage.`,
            vendor: (row.name as string) || undefined,
            timestamp: ts,
            timeAgo: timeAgoFromDate(ts),
            actionLabel: 'View Vendor',
            actionHref: '/vendor-management',
            dismissed: false,
            read: false,
            isRealtime: true,
          });
        }
      )
      .subscribe();

    // ── DD Reviews: new findings → "DD Findings"
    const ddChannel = supabase
      .channel('rt_notifications_dd')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'dd_reviews' },
        (payload) => {
          const row = payload.new as Record<string, unknown>;
          const id = `rt-dd-${notifCounterRef.current++}`;
          const ts = (row.created_at as string) || new Date().toISOString();
          addNotification({
            id,
            category: 'dd',
            severity: row.risk_tier === 'Critical' ? 'critical' : row.risk_tier === 'High' ? 'high' : 'medium',
            title: 'New DD Review Initiated',
            description: `A new due diligence review for ${row.vendor_name || 'a vendor'} has been created in the ${row.domain || ''} domain.`,
            vendor: (row.vendor_name as string) || undefined,
            timestamp: ts,
            timeAgo: timeAgoFromDate(ts),
            actionLabel: 'Open DD Review',
            actionHref: '/due-diligence-workspace',
            dismissed: false,
            read: false,
            isRealtime: true,
          });
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'dd_reviews' },
        (payload) => {
          const newRow = payload.new as Record<string, unknown>;
          const oldRow = payload.old as Record<string, unknown>;
          const becameOverdue = newRow.dd_status === 'Overdue' && oldRow.dd_status !== 'Overdue';
          if (!becameOverdue) return;
          const id = `rt-dd-overdue-${notifCounterRef.current++}`;
          const ts = (newRow.updated_at as string) || new Date().toISOString();
          addNotification({
            id,
            category: 'dd',
            severity: 'high',
            title: 'DD Assessment Now Overdue',
            description: `Due diligence review for ${newRow.vendor_name || 'a vendor'} is now overdue. Escalation required per policy.`,
            vendor: (newRow.vendor_name as string) || undefined,
            timestamp: ts,
            timeAgo: timeAgoFromDate(ts),
            actionLabel: 'Open DD Review',
            actionHref: '/due-diligence-workspace',
            dismissed: false,
            read: false,
            isRealtime: true,
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(issuesChannel);
      supabase.removeChannel(vendorsChannel);
      supabase.removeChannel(ddChannel);
    };
  }, []);

  const dismiss = (id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, dismissed: true } : n))
    );
  };

  const markRead = (id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  };

  const dismissAll = () => {
    setNotifications((prev) =>
      prev.map((n) =>
        (activeCategory === 'all' || n.category === activeCategory) ? { ...n, dismissed: true } : n
      )
    );
  };

  const markAllRead = () => {
    setNotifications((prev) =>
      prev.map((n) =>
        (activeCategory === 'all' || n.category === activeCategory) ? { ...n, read: true } : n
      )
    );
  };

  const visible = notifications.filter((n) => {
    const categoryMatch = activeCategory === 'all' || n.category === activeCategory;
    const dismissedMatch = showDismissed ? n.dismissed : !n.dismissed;
    return categoryMatch && dismissedMatch;
  });

  const unreadCount = notifications.filter((n) => !n.read && !n.dismissed).length;

  const categoryCounts: Record<string, number> = {
    all: notifications.filter((n) => !n.dismissed).length,
    critical: notifications.filter((n) => n.category === 'critical' && !n.dismissed).length,
    contracts: notifications.filter((n) => n.category === 'contracts' && !n.dismissed).length,
    dd: notifications.filter((n) => n.category === 'dd' && !n.dismissed).length,
    risk: notifications.filter((n) => n.category === 'risk' && !n.dismissed).length,
  };

  const handleExportCSV = () => {
    const rows = visible.map((n) => ({
      category: CATEGORY_CONFIG[n.category]?.label ?? n.category,
      severity: SEVERITY_LABELS[n.severity],
      title: n.title,
      description: n.description,
      vendor: n.vendor ?? '',
      timestamp: n.timestamp,
      status: n.dismissed ? 'Dismissed' : n.read ? 'Read' : 'Unread',
    } as Record<string, unknown>));
    exportCSV(NOTIF_EXPORT_COLUMNS, rows, `TPRM-Notifications-${new Date().toISOString().slice(0, 10)}`);
  };

  const handleExportPDF = () => {
    const rows = visible.map((n) => ({
      category: CATEGORY_CONFIG[n.category]?.label ?? n.category,
      severity: SEVERITY_LABELS[n.severity],
      title: n.title,
      description: n.description,
      vendor: n.vendor ?? '',
      timestamp: n.timestamp,
      status: n.dismissed ? 'Dismissed' : n.read ? 'Read' : 'Unread',
    } as Record<string, unknown>));
    exportPDF(
      'Notifications & Alerts Report',
      `City of Pleasanton TPRM · ${visible.length} notifications`,
      NOTIF_EXPORT_COLUMNS,
      rows
    );
  };

  return (
    <div className="flex flex-col h-full bg-[hsl(var(--background))]">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              href="/executive-dashboard"
              className="flex items-center gap-1.5 text-sm text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-primary))] transition-colors"
            >
              <ArrowLeft size={15} />
              <span>Home</span>
            </Link>
            <span className="text-[hsl(var(--text-muted))]">/</span>
            <div className="flex items-center gap-2">
              <Bell size={18} className="text-[hsl(var(--text-primary))]" />
              <h1 className="text-lg font-semibold text-[hsl(var(--text-primary))]">Notifications</h1>
              {unreadCount > 0 && (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400">
                  {unreadCount} unread
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Real-time status indicator */}
            <span className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded border ${realtimeConnected ? 'border-green-200 text-green-700 dark:border-green-800 dark:text-green-400' : 'border-[hsl(var(--border))] text-[hsl(var(--text-muted))]'}`}>
              <Wifi size={12} className={realtimeConnected ? 'text-green-500' : 'text-[hsl(var(--text-muted))]'} />
              {realtimeConnected ? 'Live' : 'Connecting…'}
            </span>
            <button
              onClick={handleExportCSV}
              className="btn-ghost text-xs gap-1.5"
              title="Export notifications as CSV"
            >
              <FileDown size={14} />
              CSV
            </button>
            <button
              onClick={handleExportPDF}
              className="btn-ghost text-xs gap-1.5"
              title="Export notifications as PDF"
            >
              <FileText size={14} />
              PDF
            </button>
            <button
              onClick={markAllRead}
              className="btn-ghost text-xs gap-1.5"
            >
              <CheckCheck size={14} />
              Mark all read
            </button>
            <button
              onClick={dismissAll}
              className="btn-ghost text-xs gap-1.5"
            >
              <X size={14} />
              Dismiss all
            </button>
            <button
              onClick={() => setShowDismissed((v) => !v)}
              className={`btn-ghost text-xs gap-1.5 ${showDismissed ? 'text-[hsl(var(--accent))]' : ''}`}
            >
              <Filter size={14} />
              {showDismissed ? 'Show Active' : 'Show Dismissed'}
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Category Sidebar */}
        <div className="flex-shrink-0 w-52 border-r border-[hsl(var(--border))] bg-[hsl(var(--surface))] py-4 px-3 overflow-y-auto">
          <p className="section-label px-2 mb-2">Categories</p>
          <ul className="space-y-0.5">
            {/* All */}
            <li>
              <button
                onClick={() => setActiveCategory('all')}
                className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-sm transition-colors ${
                  activeCategory === 'all' ?'bg-[hsl(var(--accent))] text-white font-medium' :'text-[hsl(var(--text-secondary))] hover:bg-[hsl(var(--surface-hover))]'
                }`}
              >
                <Bell size={15} className="flex-shrink-0" />
                <span className="flex-1 text-left">All</span>
                {categoryCounts.all > 0 && (
                  <span className={`text-2xs font-semibold px-1.5 py-0.5 rounded-full ${
                    activeCategory === 'all' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                  }`}>
                    {categoryCounts.all}
                  </span>
                )}
              </button>
            </li>
            {(Object.keys(CATEGORY_CONFIG) as Array<keyof typeof CATEGORY_CONFIG>).map((cat) => {
              const cfg = CATEGORY_CONFIG[cat];
              const CatIcon = cfg.icon;
              const count = categoryCounts[cat] ?? 0;
              const isActive = activeCategory === cat;
              return (
                <li key={cat}>
                  <button
                    onClick={() => setActiveCategory(cat)}
                    className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-sm transition-colors ${
                      isActive
                        ? 'bg-[hsl(var(--accent))] text-white font-medium'
                        : 'text-[hsl(var(--text-secondary))] hover:bg-[hsl(var(--surface-hover))]'
                    }`}
                  >
                    <CatIcon size={15} className="flex-shrink-0" />
                    <span className="flex-1 text-left">{cfg.label}</span>
                    {count > 0 && (
                      <span className={`text-2xs font-semibold px-1.5 py-0.5 rounded-full ${
                        isActive ? 'bg-white/20 text-white' : cfg.badgeBg
                      }`}>
                        {count}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Notification List */}
        <div className="flex-1 overflow-y-auto p-5">
          {visible.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <div className="w-14 h-14 rounded-full bg-[hsl(var(--surface))] border border-[hsl(var(--border))] flex items-center justify-center mb-3">
                <Bell size={24} className="text-[hsl(var(--text-muted))]" />
              </div>
              <p className="text-sm font-medium text-[hsl(var(--text-primary))]">
                {showDismissed ? 'No dismissed notifications' : 'All caught up!'}
              </p>
              <p className="text-xs text-[hsl(var(--text-muted))] mt-1">
                {showDismissed ? 'Dismissed notifications will appear here.' : 'No active notifications in this category.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {visible.map((notif) => {
                const cfg = CATEGORY_CONFIG[notif.category];
                const CatIcon = cfg.icon;
                return (
                  <div
                    key={notif.id}
                    className={`relative rounded-xl border transition-all ${cfg.border} ${cfg.bg} ${
                      !notif.read && !notif.dismissed ? 'shadow-sm' : 'opacity-80'
                    } ${notif.isRealtime && !notif.read ? 'ring-2 ring-offset-1 ring-[hsl(var(--accent))]/30' : ''}`}
                    onClick={() => markRead(notif.id)}
                  >
                    {/* Unread indicator */}
                    {!notif.read && !notif.dismissed && (
                      <span className={`absolute top-4 left-4 w-2 h-2 rounded-full ${cfg.dot} ${notif.isRealtime ? 'animate-pulse' : ''}`} />
                    )}
                    {/* Real-time badge */}
                    {notif.isRealtime && !notif.dismissed && (
                      <span className="absolute top-3 right-10 text-2xs font-semibold px-1.5 py-0.5 rounded-full bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))] border border-[hsl(var(--accent))]/20">
                        Live
                      </span>
                    )}

                    <div className="p-4 pl-8">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${cfg.bg} border ${cfg.border}`}>
                            <CatIcon size={15} className={cfg.color} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <span className="text-sm font-semibold text-[hsl(var(--text-primary))]">
                                {notif.title}
                              </span>
                              <span className={`text-2xs font-semibold px-1.5 py-0.5 rounded-full ${SEVERITY_BADGE[notif.severity]}`}>
                                {SEVERITY_LABELS[notif.severity]}
                              </span>
                            </div>
                            <p className="text-xs text-[hsl(var(--text-secondary))] leading-relaxed mb-2">
                              {notif.description}
                            </p>
                            <div className="flex flex-wrap items-center gap-3">
                              {notif.vendor && (
                                <span className="flex items-center gap-1 text-xs text-[hsl(var(--text-muted))]">
                                  <Building2 size={11} />
                                  {notif.vendor}
                                </span>
                              )}
                              <span className="flex items-center gap-1 text-xs text-[hsl(var(--text-muted))]">
                                <Clock size={11} />
                                {notif.timeAgo}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {!notif.dismissed && (
                            <>
                              <Link
                                href={notif.actionHref}
                                onClick={(e) => e.stopPropagation()}
                                className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors ${cfg.border} ${cfg.color} hover:bg-white/50 dark:hover:bg-white/5`}
                              >
                                {notif.actionLabel}
                                <ExternalLink size={11} />
                              </Link>
                              <button
                                onClick={(e) => { e.stopPropagation(); dismiss(notif.id); }}
                                className="p-1.5 rounded-lg text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-primary))] hover:bg-white/50 dark:hover:bg-white/5 transition-colors"
                                title="Dismiss"
                              >
                                <X size={14} />
                              </button>
                            </>
                          )}
                          {notif.dismissed && (
                            <span className="text-xs text-[hsl(var(--text-muted))] italic">Dismissed</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
